import { profileStorageAccountSuffix, profileStorageKey } from './profileStorageKey';

const PROFILE_EMAIL_PREFIX = 'ofrontend.profileContactEmail';
const PROFILE_PHONE_PREFIX = 'ofrontend.profileContactPhone';

/** Legacy keys cleared on logout before per-account storage existed. */
const LEGACY_EMAIL_KEY = 'ofrontend.profileContactEmail';
const LEGACY_PHONE_KEY = 'ofrontend.profileContactPhone';

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key)?.trim() || null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function safeRemove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function readKeyedField(prefix: string, legacyKey: string, loginId?: string | null): string | null {
  const keyed = safeGet(profileStorageKey(prefix, loginId));
  if (keyed) {
    return keyed;
  }
  const legacy = safeGet(legacyKey);
  if (legacy && profileStorageAccountSuffix(loginId) !== 'anonymous') {
    safeSet(profileStorageKey(prefix, loginId), legacy);
    safeRemove(legacyKey);
  }
  return legacy;
}

export function getStoredProfileEmail(loginId?: string | null): string | null {
  return readKeyedField(PROFILE_EMAIL_PREFIX, LEGACY_EMAIL_KEY, loginId);
}

export function getStoredProfilePhone(loginId?: string | null): string | null {
  return readKeyedField(PROFILE_PHONE_PREFIX, LEGACY_PHONE_KEY, loginId);
}

export function persistProfileContactHints(
  email: string | null | undefined,
  phone: string | null | undefined,
  loginId?: string | null,
): void {
  const emailValue = email?.trim();
  const phoneValue = phone?.trim();
  if (emailValue) {
    safeSet(profileStorageKey(PROFILE_EMAIL_PREFIX, loginId), emailValue);
  }
  if (phoneValue) {
    safeSet(profileStorageKey(PROFILE_PHONE_PREFIX, loginId), phoneValue);
  } else if (phone !== undefined) {
    safeRemove(profileStorageKey(PROFILE_PHONE_PREFIX, loginId));
  }
}

/** @deprecated Contact hints are per-account and intentionally kept across logout. */
export function clearStoredProfileContactHints(): void {
  safeRemove(LEGACY_EMAIL_KEY);
  safeRemove(LEGACY_PHONE_KEY);
}

function pickString(...values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) {
      return v.trim();
    }
  }
  return null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Pull email/phone from login or register JSON envelopes. */
export function contactHintsFromAuthBody(body: unknown): { email: string | null; phone: string | null } {
  if (!isPlainObject(body)) {
    return { email: null, phone: null };
  }
  let email = pickString(body.email, body.Email);
  let phone = pickString(body.phone_number, body.phoneNumber, body.phone, body.mobile, body.cell_phone);

  for (const key of ['user', 'data', 'profile', 'client', 'result'] as const) {
    const nested = body[key];
    if (!isPlainObject(nested)) {
      continue;
    }
    email = email ?? pickString(nested.email, nested.Email);
    phone =
      phone ??
      pickString(nested.phone_number, nested.phoneNumber, nested.phone, nested.mobile, nested.cell_phone);
  }

  return { email, phone };
}

export function contactHintsFromLoginIdentifier(loginIdentifier: string | null | undefined): {
  email: string | null;
  phone: string | null;
} {
  const id = loginIdentifier?.trim();
  if (!id) {
    return { email: null, phone: null };
  }
  if (id.includes('@')) {
    return { email: id, phone: null };
  }
  return { email: null, phone: id };
}

export function contactHintsFromJwt(accessToken: string | null | undefined): {
  email: string | null;
  phone: string | null;
} {
  if (!accessToken?.trim()) {
    return { email: null, phone: null };
  }
  try {
    const parts = accessToken.split('.');
    if (parts.length < 2) {
      return { email: null, phone: null };
    }
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) {
      b64 += '='.repeat(4 - pad);
    }
    const payload = JSON.parse(atob(b64)) as Record<string, unknown>;
    return {
      email: pickString(payload.email, payload.unique_name, payload.preferred_username),
      phone: pickString(payload.phone_number, payload.phone, payload.mobile),
    };
  } catch {
    return { email: null, phone: null };
  }
}

export function mergeContactHints(
  ...sources: Array<{ email: string | null; phone: string | null }>
): { email: string | null; phone: string | null } {
  let email: string | null = null;
  let phone: string | null = null;
  for (const source of sources) {
    email = email ?? source.email;
    phone = phone ?? source.phone;
  }
  return { email, phone };
}
